/**
 * Ageing a company's way, without touching a single source date
 * (docs/tally/18 §1–§2).
 *
 * A bill's age is a fact derived from TallyPrime's dates and the as-of date:
 *
 *   age by bill date   = as-of − bill date        (0 on the day of the bill)
 *   days overdue       = as-of − due date          (≤ 0: not yet due)
 *
 * A company chooses which of the two its ageing is read by, and how the days
 * are grouped into buckets. Neither choice changes a bill's dates or its age:
 * both are computed fresh from the facts every time, and the facts are never
 * written with the result.
 *
 * ## Buckets
 *
 * Stored as **upper bounds**, strictly increasing: `[45, 90, 135]` is
 * 0–45 · 46–90 · 91–135 · over 135. That form cannot express a gap or an
 * overlap at all. A person editing a table of ranges can, so ranges typed as
 * `{from, to}` are checked by `bucketsFromRanges`, which names the first gap,
 * overlap or open end it finds rather than repairing it.
 *
 * Bill-date ageing starts at 0 days. A bill dated after the as-of date
 * (TallyPrime allows it) is in the first bucket, and is reported as
 * future-dated by the checks rather than given a bucket of its own.
 *
 * Due-date ageing has a bucket before the first range: **Not yet due**
 * (0 or fewer days overdue). The ranges then count days overdue from 1.
 *
 * The product's standard windows — 0–45 · 46–90 · 91–135 · over 135 days by
 * bill date (decision D3) — are `STANDARD_BOUNDARIES`, and `bucketIndex` with
 * them places every age exactly where `ageingBucket()` in tallySource.ts does
 * (a test holds the two equal at every boundary).
 */
import type { SourceAccountFact, SourceFacts } from './receivablesFacts.js';
import { daysFrom, fromPaise, toPaise } from './receivablesFacts.js';

export type AgeingBasis = 'bill_date' | 'due_date';

/** What a due-date ageing does with a bill TallyPrime gave no due date. */
export type MissingDueDateRule = 'use_bill_date' | 'unaged';

export interface AgeingConfig {
    basis: AgeingBasis;
    /** Upper bounds of every bucket but the last, strictly increasing, in days. */
    boundaries: number[];
    /** Due-date basis only. TallyPrime's own rule — no credit period means due on the bill date — is `use_bill_date`. */
    missingDueDate: MissingDueDateRule;
}

/** 0–45 · 46–90 · 91–135 · over 135: the product's standard windows (D3). */
export const STANDARD_BOUNDARIES: readonly number[] = [45, 90, 135];

export const AGEING_LIMITS = {
    /** Buckets, including the open-ended last one. */
    minBuckets: 2,
    maxBuckets: 8,
    /** The largest upper bound a bucket may have: ten years. */
    maxDays: 3650,
} as const;

export interface AgeingPreset {
    id: 'standard_45' | 'monthly_30' | 'fortnightly_15';
    label: string;
    boundaries: readonly number[];
}

export const AGEING_PRESETS: readonly AgeingPreset[] = [
    { id: 'standard_45', label: '45-day windows (0–45, 46–90, 91–135, over 135)', boundaries: STANDARD_BOUNDARIES },
    { id: 'monthly_30', label: '30-day windows (0–30, 31–60, 61–90, 91–120, over 120)', boundaries: [30, 60, 90, 120] },
    { id: 'fortnightly_15', label: 'Short windows (0–15, 16–30, 31–45, 46–75, over 75)', boundaries: [15, 30, 45, 75] },
];

export const DEFAULT_AGEING: AgeingConfig = {
    basis: 'bill_date',
    boundaries: [...STANDARD_BOUNDARIES],
    missingDueDate: 'use_bill_date',
};

/* ------------------------------ validation ------------------------------ */

export type AgeingProblemCode =
    | 'too_few' | 'too_many' | 'not_whole_days' | 'first_start' | 'empty_range'
    | 'gap' | 'overlap' | 'open_end_not_last' | 'last_not_open' | 'too_large' | 'not_increasing';

export interface AgeingProblem {
    code: AgeingProblemCode;
    /** The bucket (0-based) the problem is in, when it is in one. */
    bucket?: number;
    message: string;
}

/** A bucket as a person types it: from and to, inclusive; `to: null` is "and over". */
export interface BucketRange { from: number; to: number | null }

const whole = (n: unknown): n is number => typeof n === 'number' && Number.isInteger(n);

/** The first day of the first range: 0 days old by bill date, 1 day overdue by due date. */
export const firstDay = (basis: AgeingBasis): number => (basis === 'bill_date' ? 0 : 1);

/**
 * Upper bounds from ranges, checked as typed. Every problem is reported, in
 * order; nothing is repaired. A gap ("46–90" after "0–40") and an overlap
 * ("40–90" after "0–45") are named as such.
 */
export function bucketsFromRanges(ranges: readonly BucketRange[], basis: AgeingBasis): { boundaries: number[] | null; problems: AgeingProblem[] } {
    const problems: AgeingProblem[] = [];
    if (ranges.length < AGEING_LIMITS.minBuckets) problems.push({ code: 'too_few', message: `Use at least ${AGEING_LIMITS.minBuckets} buckets.` });
    if (ranges.length > AGEING_LIMITS.maxBuckets) problems.push({ code: 'too_many', message: `Use at most ${AGEING_LIMITS.maxBuckets} buckets.` });
    ranges.forEach((r, i) => {
        const last = i === ranges.length - 1;
        if (!whole(r.from) || (r.to !== null && !whole(r.to))) {
            problems.push({ code: 'not_whole_days', bucket: i, message: `Bucket ${i + 1}: use whole days.` });
            return;
        }
        if (i === 0 && r.from !== firstDay(basis)) {
            problems.push({ code: 'first_start', bucket: 0, message: basis === 'bill_date'
                ? 'The first bucket starts at 0 days, so a bill dated today has a bucket.'
                : 'The first bucket starts at 1 day overdue. Bills not yet due are shown on their own.' });
        }
        if (r.to === null && !last) problems.push({ code: 'open_end_not_last', bucket: i, message: `Bucket ${i + 1} is open-ended, so it must be the last.` });
        if (last && r.to !== null) problems.push({ code: 'last_not_open', bucket: i, message: `The last bucket has no upper limit ("${r.from} and over"), so no bill is left without one.` });
        if (r.to !== null && r.to < r.from) problems.push({ code: 'empty_range', bucket: i, message: `Bucket ${i + 1} ends before it starts.` });
        if (r.to !== null && r.to > AGEING_LIMITS.maxDays) problems.push({ code: 'too_large', bucket: i, message: `Bucket ${i + 1} reaches past ${AGEING_LIMITS.maxDays} days.` });
        if (i > 0) {
            const prev = ranges[i - 1]!;
            if (prev.to !== null && whole(prev.to)) {
                if (r.from > prev.to + 1) problems.push({ code: 'gap', bucket: i, message: `Days ${prev.to + 1}–${r.from - 1} are in no bucket. Start bucket ${i + 1} at ${prev.to + 1}.` });
                if (r.from <= prev.to) problems.push({ code: 'overlap', bucket: i, message: `Bucket ${i + 1} starts at ${r.from}, inside bucket ${i} (which ends at ${prev.to}). Start it at ${prev.to + 1}.` });
            }
        }
    });
    if (problems.length) return { boundaries: null, problems };
    return { boundaries: ranges.slice(0, -1).map(r => r.to as number), problems };
}

/** Checks stored upper bounds (the form settings keep). */
export function validateBoundaries(boundaries: unknown, basis: AgeingBasis): AgeingProblem[] {
    if (!Array.isArray(boundaries)) return [{ code: 'too_few', message: 'The buckets are missing.' }];
    const problems: AgeingProblem[] = [];
    if (boundaries.length + 1 < AGEING_LIMITS.minBuckets) problems.push({ code: 'too_few', message: `Use at least ${AGEING_LIMITS.minBuckets} buckets.` });
    if (boundaries.length + 1 > AGEING_LIMITS.maxBuckets) problems.push({ code: 'too_many', message: `Use at most ${AGEING_LIMITS.maxBuckets} buckets.` });
    let prev = firstDay(basis) - 1;
    boundaries.forEach((b, i) => {
        if (!whole(b)) { problems.push({ code: 'not_whole_days', bucket: i, message: `Bucket ${i + 1}: use whole days.` }); return; }
        if (b <= prev) problems.push({ code: 'not_increasing', bucket: i, message: `Bucket ${i + 1} must end after bucket ${i} does.` });
        if (b > AGEING_LIMITS.maxDays) problems.push({ code: 'too_large', bucket: i, message: `Bucket ${i + 1} reaches past ${AGEING_LIMITS.maxDays} days.` });
        prev = b;
    });
    return problems;
}

/** The ranges stored upper bounds stand for, for showing and editing. */
export function rangesFromBoundaries(boundaries: readonly number[], basis: AgeingBasis): BucketRange[] {
    const out: BucketRange[] = [];
    let from = firstDay(basis);
    for (const b of boundaries) { out.push({ from, to: b }); from = b + 1; }
    out.push({ from, to: null });
    return out;
}

/** "0–45 days", "Over 135 days"; by due date "1–30 days overdue", "Over 120 days overdue". */
export function bucketLabels(config: Pick<AgeingConfig, 'basis' | 'boundaries'>): string[] {
    const suffix = config.basis === 'due_date' ? ' days overdue' : ' days';
    return rangesFromBoundaries(config.boundaries, config.basis).map(r =>
        r.to === null ? `Over ${r.from - 1}${suffix}` : `${r.from}–${r.to}${suffix}`);
}

/**
 * The bucket a number of days falls in. Bill date: anything at or below the
 * first bound (including a future date) is the first bucket. Due date: call
 * only for 1 or more days overdue — `ageBill` sends the rest to Not yet due.
 */
export function bucketIndex(days: number, boundaries: readonly number[]): number {
    for (let i = 0; i < boundaries.length; i++) if (days <= boundaries[i]!) return i;
    return boundaries.length;
}

/* ------------------------------ one bill ------------------------------- */

export type BillPlacement =
    | { kind: 'bucket'; bucket: number }
    | { kind: 'not_yet_due' }
    | { kind: 'unaged_no_due_date' };

export interface AgedBill {
    /** By bill date, from the source dates; the same whatever the settings. */
    ageDays: number;
    /** By due date; null when TallyPrime gave no due date. Never filled in. */
    overdueDays: number | null;
    placement: BillPlacement;
}

/** Where one bill sits under a configuration. The bill is not changed. */
export function ageBill(bill: { billDate: string; dueDate?: string }, asOf: string, config: AgeingConfig): AgedBill {
    const ageDays = daysFrom(bill.billDate, asOf);
    const overdueDays = bill.dueDate ? daysFrom(bill.dueDate, asOf) : null;
    if (config.basis === 'bill_date') return { ageDays, overdueDays, placement: { kind: 'bucket', bucket: bucketIndex(ageDays, config.boundaries) } };
    const overdue = overdueDays ?? (config.missingDueDate === 'use_bill_date' ? ageDays : null);
    if (overdue === null) return { ageDays, overdueDays, placement: { kind: 'unaged_no_due_date' } };
    if (overdue <= 0) return { ageDays, overdueDays, placement: { kind: 'not_yet_due' } };
    return { ageDays, overdueDays, placement: { kind: 'bucket', bucket: bucketIndex(overdue, config.boundaries) } };
}

/* ------------------------------ histogram ------------------------------ */

/**
 * The debit side of a book's open bills, by day: for each age (bill date) and
 * each number of days overdue (due date), how many bills and how much. It is
 * all any bucket layout needs, so a preview of any layout is exact without a
 * single bill leaving the server — and it names no customer.
 *
 * Credit-side bills (credit notes as new references, advances) are not in it:
 * they are shown apart, never netted into a bucket by their own date.
 */
export interface AgeHistogram {
    asOf: string;
    /** [days, paise, bills], ascending by days. */
    byBillDate: [number, number, number][];
    /** [days overdue, paise, bills], ascending. Bills without a due date are not here. */
    byDueDate: [number, number, number][];
    /** Debit bills TallyPrime gave no due date: their total, and their ages by bill date. */
    noDueDate: { paise: number; bills: number; byBillDate: [number, number, number][] };
}

export function ageHistogram(facts: Pick<SourceFacts, 'asOf'> & { accounts: readonly Pick<SourceAccountFact, 'bills'>[] }): AgeHistogram {
    const byBill = new Map<number, [number, number]>();
    const byDue = new Map<number, [number, number]>();
    const noDueByBill = new Map<number, [number, number]>();
    const add = (m: Map<number, [number, number]>, day: number, p: number) => { const e = m.get(day) ?? [0, 0]; m.set(day, [e[0] + p, e[1] + 1]); };
    for (const account of facts.accounts) {
        for (const bill of account.bills) {
            const p = toPaise(bill.pending);
            if (p <= 0) continue;
            const age = daysFrom(bill.billDate, facts.asOf);
            add(byBill, age, p);
            if (bill.dueDate) add(byDue, daysFrom(bill.dueDate, facts.asOf), p);
            else add(noDueByBill, age, p);
        }
    }
    const rows = (m: Map<number, [number, number]>) => [...m.entries()].sort((a, b) => a[0] - b[0]).map(([d, [p, n]]) => [d, p, n] as [number, number, number]);
    const noDue = rows(noDueByBill);
    return {
        asOf: facts.asOf, byBillDate: rows(byBill), byDueDate: rows(byDue),
        noDueDate: { paise: noDue.reduce((s, r) => s + r[1], 0), bills: noDue.reduce((s, r) => s + r[2], 0), byBillDate: noDue },
    };
}

export interface BucketTotal { label: string; amount: number; bills: number }

export interface BucketPreview {
    basis: AgeingBasis;
    buckets: BucketTotal[];
    /** Due date only. */
    notYetDue: { amount: number; bills: number };
    /** Due date with `unaged`: debit bills without a due date, kept out of every bucket. */
    unagedNoDueDate: { amount: number; bills: number };
    /** Debit bills placed: always the whole of the histogram, whatever the layout. */
    total: { amount: number; bills: number };
}

/** Exactly what a layout would show, from the histogram alone. */
export function previewBuckets(hist: AgeHistogram, config: AgeingConfig): BucketPreview {
    const labels = bucketLabels(config);
    const paise = labels.map(() => 0);
    const counts = labels.map(() => 0);
    let notYetDueP = 0, notYetDueN = 0, unagedP = 0, unagedN = 0, totalP = 0, totalN = 0;
    const place = (days: number, p: number, n: number) => { const i = bucketIndex(days, config.boundaries); paise[i] += p; counts[i] += n; };
    if (config.basis === 'bill_date') {
        for (const [days, p, n] of hist.byBillDate) place(days, p, n);
    } else {
        const overdue = (days: number, p: number, n: number) => {
            if (days <= 0) { notYetDueP += p; notYetDueN += n; } else place(days, p, n);
        };
        for (const [days, p, n] of hist.byDueDate) overdue(days, p, n);
        if (config.missingDueDate === 'unaged') { unagedP += hist.noDueDate.paise; unagedN += hist.noDueDate.bills; }
        // No due date, read as due on the bill date (TallyPrime's own rule): its days overdue are its age.
        else for (const [days, p, n] of hist.noDueDate.byBillDate) overdue(days, p, n);
    }
    for (const [, p, n] of hist.byBillDate) { totalP += p; totalN += n; }
    const preview: BucketPreview = {
        basis: config.basis,
        buckets: labels.map((label, i) => ({ label, amount: fromPaise(paise[i]!), bills: counts[i]! })),
        notYetDue: { amount: fromPaise(notYetDueP), bills: notYetDueN },
        unagedNoDueDate: { amount: fromPaise(unagedP), bills: unagedN },
        total: { amount: fromPaise(totalP), bills: totalN },
    };
    return preview;
}
